<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from "vue";

// A lightweight canvas signature pad for desk sign-off (brief §1.5 — the
// approving CRE / PM digitally signs off). Emits the drawn signature as a
// PNG data URI via v-model, matching the data:image/png strings the mobile
// app captures, so the web and PDF render them identically.
const model = defineModel<string | null>({ default: null });

const canvas = ref<HTMLCanvasElement | null>(null);
let ctx: CanvasRenderingContext2D | null = null;
let drawing = false;
let dirty = false;

function pos(e: PointerEvent): { x: number; y: number } {
  const rect = canvas.value!.getBoundingClientRect();
  return { x: e.clientX - rect.left, y: e.clientY - rect.top };
}

function start(e: PointerEvent): void {
  if (!ctx) return;
  drawing = true;
  canvas.value!.setPointerCapture(e.pointerId);
  const { x, y } = pos(e);
  ctx.beginPath();
  ctx.moveTo(x, y);
}

function move(e: PointerEvent): void {
  if (!drawing || !ctx) return;
  const { x, y } = pos(e);
  ctx.lineTo(x, y);
  ctx.stroke();
  dirty = true;
}

function end(): void {
  if (!drawing) return;
  drawing = false;
  if (dirty && canvas.value) model.value = canvas.value.toDataURL("image/png");
}

function clear(): void {
  if (!ctx || !canvas.value) return;
  ctx.clearRect(0, 0, canvas.value.width, canvas.value.height);
  dirty = false;
  model.value = null;
}

defineExpose({ clear });

onMounted(() => {
  const el = canvas.value!;
  // Match the backing store to the displayed size for crisp strokes.
  el.width = el.clientWidth;
  el.height = el.clientHeight;
  ctx = el.getContext("2d");
  if (ctx) {
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#111827";
  }
});

onBeforeUnmount(() => {
  ctx = null;
});
</script>

<template>
  <div class="grid gap-2">
    <canvas
      ref="canvas"
      class="h-32 w-full touch-none rounded-md border border-neutral-300 bg-white"
      @pointerdown="start"
      @pointermove="move"
      @pointerup="end"
      @pointerleave="end"
    />
    <button
      type="button"
      class="justify-self-start text-xs text-neutral-500 hover:text-neutral-900"
      @click="clear"
    >
      Clear signature
    </button>
  </div>
</template>
